/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import {
    useState, useEffect, useContext, useCallback
} from 'react';
import {
    Card, CardContent
} from 'shared/components/ui/card';
import {
    RecordsHeader
} from './RecordsHeader';
import {
    RecordsContent
} from './RecordsContent';
import {
    Context
} from 'shared/context/GlobalState';
import {
    useRecordsData
} from './RecordsData';
import {
    RecordsPagination
} from './recordsPagination';
import AlertModal from 'shared/components/Alertmodal';
import {
 Column,Row
} from './types/builderRecords.types';

interface RecordsTableProps {
    tableId: string | null;
}

export function RecordsTable({
    tableId
}: RecordsTableProps) {
    const {
        loading,
        setLoading,
        addRecords,
        modifyRecords,
        deleteRecords,
        fetchAllRecords,
        fetchSchema
    } = useRecordsData(tableId);

    const {
        addNewNotifcation
    } = useContext(Context);

    const [columns, setColumns] = useState<Column[]>([]);
    const [rows, setRows] = useState<Row[]>([]);
    const [displayedRows, setDisplayedRows] = useState<Row[]>([]);

    const [createdRows, setCreatedRows] = useState<Row[]>([]);
    const [updatedRows, setUpdatedRows] = useState<Row[]>([]);
    const [deletedRowIds, setDeletedRowIds] = useState<string[]>([]);
    const [editingCell, setEditingCell] = useState<{ rowId: string; colId: string } | null>(null);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [delModal, setDelModal] = useState(false);
    const [delBtn] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterColumn, setFilterColumn] = useState<string | null>(null);
    const [filterValue, setFilterValue] = useState('');
    const [groupByColumn, setGroupByColumn] = useState<string | null>(null);
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);

    const isAddingNewRecord = createdRows.length > 0;

    const hasUnsavedChanges =
        isAddingNewRecord || updatedRows.length > 0 || deletedRowIds.length > 0;

    const loadTableData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchAllRecords(currentPage);

            const {
                records, totalRecords
            } = response;

            setTotalRecords(totalRecords);

            const schemaResponse = await fetchSchema();
            const schemas = schemaResponse.data.schemas;
            const userSchema = schemas.find((schema) => schema.tableName === tableId);
            const fetchedColumns = userSchema?.columns || [];
            setColumns(fetchedColumns || []);
            setRows(records || []);
            setCreatedRows([]);
            setUpdatedRows([]);
            setDeletedRowIds([]);
            setSelectedRows([]);

            // Update pagination-related states
            setTotalPages(Math.ceil(totalRecords / 10));
            if (records.length < 1) {
                setLoading(false);
            }
        } catch (error) {
            console.error('Failed to load table data:', error);
            addNewNotifcation('Failed to load table data', 'danger');
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, [fetchAllRecords, tableId, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
        loadTableData();
    }, [tableId]);

    useEffect(() => {
        loadTableData();
    }, [currentPage]);

    useEffect(() => {
        let processed = [...rows];

        updatedRows.forEach((update) => {
            const index = processed.findIndex((row) => row.id === update.id);
            if (index !== -1) {
                processed[index] = {
                    ...processed[index],
                    ...update
                };
            }
        });

        processed = processed.filter((row) => !deletedRowIds.includes(row.id));
        processed = [...processed, ...createdRows];

        if (searchTerm) {
            processed = processed.filter((row) =>
                columns.some((col) =>
                    String(row[col.columnName] || '').toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }

        if (filterColumn && filterValue) {
            processed = processed.filter((row) =>
                String(row[filterColumn] || '')
                    .toLowerCase()
                    .includes(filterValue.toLowerCase())
            );
        }

        if (groupByColumn) {
            const grouped = processed.reduce((acc, row) => {
                const groupValue = row[groupByColumn] || 'Ungrouped';
                if (!acc[groupValue]) acc[groupValue] = [];
                acc[groupValue].push(row);
                return acc;
            }, {});

            processed = Object.entries(grouped).flatMap(([group, groupRows], i) => [
                {
                    id: `group-${i}-${group}`,
                    isGroupHeader: true,
                    groupValue: group
                },
                ...groupRows
            ]);
        }

        if (sortColumn) {
            processed.sort((a, b) => {
                if (a.isGroupHeader || b.isGroupHeader) return 0;

                const aVal = a[sortColumn];
                const bVal = b[sortColumn];

                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
                }
                return sortDirection === 'asc'
                    ? String(aVal || '').localeCompare(String(bVal || ''))
                    : String(bVal || '').localeCompare(String(aVal || ''));
            });
        }

        setDisplayedRows(processed);
    }, [
        rows,
        columns,
        searchTerm,
        filterColumn,
        filterValue,
        groupByColumn,
        sortColumn,
        sortDirection,
        createdRows,
        updatedRows,
        deletedRowIds
    ]);

    const generateUniqueId = useCallback(() => {
        return `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }, []);

    const handleCellClick = useCallback((rowId: string, colId: string) => {
        setEditingCell({
            rowId,
            colId
        });
    }, []);

    const handleCellChange = useCallback(
        (rowId: string, colId: string, value: any, colDataType: string | null = null) => {
            let newValue = value;

            if (colDataType === 'enum') {
                if (Array.isArray(value)) {
                    newValue = value;
                } else if (value) {
                    newValue = [value];
                } else {
                    newValue = [];
                }
            }

            if (createdRows.some((row) => row.id === rowId)) {
                setCreatedRows((prev) =>
                    prev.map((row) =>
                        row.id === rowId ? {
                            ...row,
                            [colId]: newValue
                        } : row
                    )
                );
            } else {
                setDisplayedRows((prev) =>
                    prev.map((row) =>
                        row.id === rowId && !row.isGroupHeader
                            ? {
                                ...row,
                                [colId]: newValue
                            }
                            : row
                    )
                );
            }
        },
        [createdRows]
    );

    const handleCellBlur = useCallback(
        (rowId: string, colId: string, value: any) => {
            const original = rows.find((r) => r.id === rowId);
            if (!original || original[colId] === value) return;

            setUpdatedRows((prev) => {
                const existing = prev.find((r) => r.id === rowId);
                const updated = {
                    ...(existing || {}),
                    id: rowId,
                    recordId: original?.recordId,
                    [colId]: value
                };
                return existing
                    ? prev.map((r) => (r.id === rowId ? updated : r))
                    : [...prev, updated];
            });

            setEditingCell(null);
        },
        [rows]
    );

    const handleAddRow = useCallback(() => {
        const newRow = {
            id: generateUniqueId(),
            isDraft: true
        };
        columns.forEach((col) => {
            newRow[col.id] = '';
        });
        setCreatedRows((prev) => {
            const updated = [...prev, newRow];
             // Log the updated state of createdRows
            return updated;
        });
    }, [columns]);

    // const handleEditRow = useCallback(
    //     (rowId: string) => {
    //         const firstVisible = columns.find((col) => !hiddenColumns.includes(col.id));
    //         if (firstVisible) {
    //             handleCellClick(rowId, firstVisible.id);
    //         }
    //     },
    //     [columns, hiddenColumns, handleCellClick]
    // );

    const handleDeleteRow = async (rowIds: string | string[]) => {
        let toDelete;
        let ids;
        if (tableId !== 'User') {
            ids = Array.isArray(rowIds) ? rowIds : [rowIds];
            toDelete = rows.filter((row) => ids.includes(row.id));
            const drafts = createdRows.filter((row) => ids.includes(row.id));

            if (drafts.length) {
                setCreatedRows((prev) => prev.filter((row) => !ids.includes(row.id)));
            }
        } else {
            ids = Array.isArray(rowIds) ? rowIds : [rowIds];
            toDelete = rows.filter((row) => ids.includes(row.user_id));
            const drafts = createdRows.filter((row) => ids.includes(row.user_id));

            if (drafts.length) {
                setCreatedRows((prev) => prev.filter((row) => !ids.includes(row.user_id)));
            }
        }
        if (toDelete.length) {
            try {
                await deleteRecords(toDelete);
                addNewNotifcation('Records deleted', 'success');
            } catch (e) {
                console.error('Error deleting:', e);
                addNewNotifcation('Error deleting records', 'danger');
            }
        }

        await loadTableData();
        setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)));
    };

    const handleSelectRow = useCallback((rowId: string) => {
        setSelectedRows((prev) =>
            prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
        );
    }, []);

    // const handleSelectAll = useCallback(() => {
    //     const ids = displayedRows.filter((r) => !r.isGroupHeader).map((r) => r.id);
    //     setSelectedRows((prev) => (prev.length === ids.length ? [] : ids));
    // }, [displayedRows]);

    const handleSaveCreatedRecords = async () => {
        try {
            await addRecords(
                createdRows.map(({
                     ...cleaned
                }) => cleaned)
            );
            addNewNotifcation('Records added', 'success');
        } catch (e) {
            addNewNotifcation('Add failed: ' + e.message, 'danger');
        }
        await loadTableData();
    };

    const handleSaveModifiedRecords = async () => {
        try {

            await modifyRecords(updatedRows);
            addNewNotifcation('Records updated', 'success');
        } catch (e) {
            addNewNotifcation('Update failed: ' + e.message, 'danger');
        }
        await loadTableData();
    };

    const handleDiscardChanges = async () => {
        await loadTableData();
        addNewNotifcation('Changes discarded', 'warning');
    };

    if (loading) {
        return (
            <Card className="border shadow-sm h-full w-full">
                <CardContent className="p-6 flex items-center justify-center">
                    <div className="text-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent mx-auto mb-4"></div>
                        <p>Loading table data...</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

return (
    <Card className="border shadow-sm h-full w-full">
        <CardContent className="p-0 h-full w-full flex flex-col">
            <AlertModal
                loading={delBtn}
                openModal={delModal}
                setopenModal={setDelModal}
                title={`Are you sure you want to delete ${selectedRows.length} records?`}
                onDelete={async () => {
                    await handleDeleteRow(selectedRows);
                    setDelModal(false);
                }}
            />

            <div className="flex flex-1 flex-col h-full w-full">
                <RecordsHeader
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    filterColumn={filterColumn}
                    setFilterColumn={setFilterColumn}
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    groupByColumn={groupByColumn}
                    setGroupByColumn={setGroupByColumn}
                    sortColumn={sortColumn}
                    setSortColumn={setSortColumn}
                    sortDirection={sortDirection}
                    setSortDirection={setSortDirection}
                    hiddenColumns={hiddenColumns}
                    setHiddenColumns={setHiddenColumns}
                    columns={columns}
                    hasUnsavedChanges={hasUnsavedChanges}
                    onSaveCreatedRecords={handleSaveCreatedRecords}
                    onSaveModifiedRecords={handleSaveModifiedRecords}
                    onDiscardChanges={handleDiscardChanges}
                    updatedRows={updatedRows}
                    createdRows={createdRows}
                    selectedRows={selectedRows}
                    setDelModal={setDelModal}
                    isDeleting={delBtn}
                    setRowsToDelete={setSelectedRows}
                    onRefreshRecords={loadTableData}
                    isLoading={loading}
                    onAddRow={handleAddRow}
                    tableId={tableId}
                />

                <RecordsContent
                    columns={columns}
                    displayedRows={displayedRows}
                    hiddenColumns={hiddenColumns}
                    selectedRows={selectedRows}
                    editingCell={editingCell}
                    onCellClick={handleCellClick}
                    onCellChange={handleCellChange}
                    onCellBlur={handleCellBlur}
                    // onEditRow={handleEditRow}
                    onDeleteRow={handleDeleteRow}
                    onSelectRow={handleSelectRow}
                    // onSelectAll={handleSelectAll}
                    // onAddRow={handleAddRow}
                    updatedRows={updatedRows}
                    createdRows={createdRows}
                    isAddingNewRecord={isAddingNewRecord}
                    // isLoading={loading}
                    tableId={tableId}
                />
            </div>

            <RecordsPagination
                currentPage={currentPage}
                setCurrentPage={(page) => {
                    setCurrentPage(page);
                }}
                totalPages={totalPages}
                totalRecords={totalRecords}
            />
        </CardContent>
    </Card>
);

}
